/**
 * SharePoint Service - Client-side SharePoint operations via Microsoft Graph
 * Replaces PnPjs implementation to use standard Graph API
 */

import { Client } from '@microsoft/microsoft-graph-client';
import { getGraphAccessToken } from './authService';

/**
 * Initialize Graph Client with Auth Provider
 */
export async function getGraphClient(): Promise<Client> {
    const token = await getGraphAccessToken();
    return Client.init({
        authProvider: (done) => {
            done(null, token);
        },
    });
}

/**
 * Result of site verification
 */
export interface SiteVerificationResult {
    isOwner: boolean;
    siteName?: string;
    error?: string;
    siteId?: string;
    webUrl?: string;
}

let _cachedRootHostname: string | null = null;

/**
 * Resolve the tenant's SharePoint root hostname (e.g. "contoso.sharepoint.com") via
 * Graph's `/sites/root` alias, using an already-acquired Graph token. Needed because
 * the classic SharePoint REST API's OAuth resource is host-specific (unlike Graph,
 * which uses one fixed audience), so the hostname must be known before requesting an
 * AllSites.Read-scoped token for it. Cached for the session since it never changes.
 */
export async function getSharePointRootHostname(graphAccessToken: string): Promise<string | null> {
    if (_cachedRootHostname) return _cachedRootHostname;

    try {
        const client = Client.init({ authProvider: (done) => done(null, graphAccessToken) });
        const site = await client.api('/sites/root').select('siteCollection,webUrl').get();
        const hostname: string | null =
            site?.siteCollection?.hostname ?? (site?.webUrl ? new URL(site.webUrl).hostname : null);
        if (hostname) _cachedRootHostname = hostname;
        return hostname;
    } catch (error) {
        console.warn('[sharePointService] Could not resolve SharePoint root hostname:', error);
        return null;
    }
}

/**
 * Extract hostname and relative path from a simplified SharePoint URL
 */
export function parseSiteUrl(url: string): { hostname: string; path: string } {
    try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname;
        let path = urlObj.pathname;
        if (path.endsWith('/')) path = path.slice(0, -1);
        if (path === '') path = '/';
        return { hostname, path };
    } catch {
        throw new Error(`Invalid URL: ${url}`);
    }
}

/**
 * Verify if the specific SharePoint site exists and the user has access
 * Uses Graph API to resolve the site
 */
export async function verifySiteOwnership(siteUrl: string): Promise<SiteVerificationResult> {
    try {
        const client = await getGraphClient();
        const { hostname, path } = parseSiteUrl(siteUrl);

        // Construct Graph API path for site lookup
        // Pattern: /sites/{hostname}:/{server-relative-path}
        const apiPath = `/sites/${hostname}:${path}`;

        console.log(`[sharePointService] Verifying site: ${apiPath}`);

        try {
            const site = await client.api(apiPath).select('id,displayName,webUrl').get();
            if (site && site.id) {
                return {
                    isOwner: true,
                    siteName: site.displayName,
                    siteId: site.id,
                    webUrl: site.webUrl
                };
            }
        } catch (apiError: any) {
            if (apiError.statusCode === 404) {
                return { isOwner: false, error: 'Site not found.' };
            }
            throw apiError;
        }

        return { isOwner: false, error: 'Site not found or access denied.' };

    } catch (error: any) {
        console.error('[sharePointService] Site verification failed:', error);
        return {
            isOwner: false,
            error: error.message || 'Failed to access site. Ensure you have permissions.'
        };
    }
}

/**
 * Provision all required lists on the primary SharePoint site during onboarding.
 *
 * Lists provisioned:
 *   1. WVHealth_ToolConfig      – tenant configuration (source of truth for SetupComplete)
 *   2. WVHealth_CoreSettings    – Copilot analysis configuration (model, thresholds, etc.)
 *   3. WV_SearchEmbeddingsCache – page embeddings & analysis results
 *
 * This function is idempotent: it checks for list/column/item existence before
 * creating, so it is safe to call on re-runs or when sites are re-added.
 *
 * @param siteUrl         - Primary SharePoint site URL (all lists will be created HERE)
 * @param tenantId        - Azure AD tenant ID (stored in WVHealth_ToolConfig)
 * @param orgName         - Organisation display name
 * @param allowedSiteUrls - Full list of licensed site URLs (stored in WVHealth_ToolConfig)
 */
export async function provisionAllRequiredLists(
    siteUrl: string,
    tenantId: string,
    orgName: string,
    allowedSiteUrls: string[]
): Promise<{ success: boolean; error?: string }> {
    try {
        const client = await getGraphClient();
        const { hostname, path } = parseSiteUrl(siteUrl);

        // ── Resolve Site ID ──────────────────────────────────────────────────
        const siteResp = await client.api(`/sites/${hostname}:${path}`).select('id').get();
        if (!siteResp?.id) throw new Error('Could not resolve site ID');
        const siteId = siteResp.id;

        // ── Helper: get-or-create a generic SharePoint list ──────────────────
        const ensureList = async (listName: string): Promise<string> => {
            const lists = await client.api(`/sites/${siteId}/lists`)
                .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
                .filter(`displayName eq '${listName}'`)
                .select('id')
                .get();

            if (lists.value && lists.value.length > 0) {
                console.log(`[sharePointService] List "${listName}" already exists.`);
                return lists.value[0].id as string;
            }

            console.log(`[sharePointService] Creating list "${listName}"...`);
            const newList = await client.api(`/sites/${siteId}/lists`).post({
                displayName: listName,
                list: { template: 'genericList' }
            });
            return newList.id as string;
        };

        // ── Helper: add columns that don't yet exist ─────────────────────────
        type ColSpec = { name: string; displayName: string; type: 'text' | 'multiline' | 'boolean' | 'dateTime' | 'number' };
        const ensureColumns = async (listId: string, cols: ColSpec[]): Promise<void> => {
            const existingCols = await client.api(`/sites/${siteId}/lists/${listId}/columns`).select('name').get();
            const existing = new Set<string>(existingCols.value.map((c: any) => c.name));

            for (const col of cols) {
                if (existing.has(col.name)) continue;
                console.log(`[sharePointService]   Adding column "${col.name}"...`);
                const def: any = { name: col.name, displayName: col.displayName };
                if (col.type === 'text') def.text = {};
                else if (col.type === 'multiline') def.text = { allowMultipleLines: true };
                else if (col.type === 'boolean') def.boolean = {};
                else if (col.type === 'dateTime') def.dateTime = {};
                else if (col.type === 'number') def.number = {};
                await client.api(`/sites/${siteId}/lists/${listId}/columns`).post(def);
            }
        };

        // ════════════════════════════════════════════════════════════════════
        // 1. WVHealth_ToolConfig
        // ════════════════════════════════════════════════════════════════════
        console.log('[sharePointService] Provisioning WVHealth_ToolConfig...');
        const toolConfigListId = await ensureList('WVHealth_ToolConfig');

        await ensureColumns(toolConfigListId, [
            { name: 'ConfigKey', displayName: 'Config Key', type: 'text' },
            { name: 'ConfigValue', displayName: 'Config Value', type: 'text' },
            { name: 'Description', displayName: 'Description', type: 'text' },
            { name: 'TenantId', displayName: 'Tenant Id', type: 'text' },
            { name: 'OrganizationName', displayName: 'Organization Name', type: 'text' },
            { name: 'PrimarySiteUrl', displayName: 'Primary Site Url', type: 'text' },
            { name: 'EvaluationMatrixVersion', displayName: 'Evaluation Matrix Version', type: 'text' },
            { name: 'ProvisionedBy', displayName: 'Provisioned By', type: 'text' },
            { name: 'ProvisionedAt', displayName: 'Provisioned At', type: 'dateTime' },
            { name: 'SetupComplete', displayName: 'Setup Complete', type: 'boolean' },
            { name: 'AllowProductTelemetry', displayName: 'Allow Product Telemetry', type: 'boolean' },
            { name: 'AllowedSiteUrls', displayName: 'Allowed Site Urls', type: 'multiline' },
            { name: 'FeatureFlags', displayName: 'Feature Flags', type: 'multiline' },
        ]);

        // Seed TENANT_CONFIG item if missing
        const tcItems = await client.api(`/sites/${siteId}/lists/${toolConfigListId}/items`)
            .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
            .expand('fields')
            .filter("fields/ConfigKey eq 'TENANT_CONFIG'")
            .get();

        if (!tcItems.value || tcItems.value.length === 0) {
            await client.api(`/sites/${siteId}/lists/${toolConfigListId}/items`).post({
                fields: {
                    Title: 'Tenant Configuration',
                    ConfigKey: 'TENANT_CONFIG',
                    TenantId: tenantId,
                    OrganizationName: orgName,
                    PrimarySiteUrl: siteUrl,
                    EvaluationMatrixVersion: '1.0',
                    ProvisionedBy: 'WebVineSystem',
                    SetupComplete: false,
                    AllowProductTelemetry: true,
                    AllowedSiteUrls: JSON.stringify(allowedSiteUrls),
                    FeatureFlags: JSON.stringify({}),
                    ProvisionedAt: new Date().toISOString(),
                    Description: 'Initial setup record created by Onboarding Wizard'
                }
            });
            console.log('[sharePointService] WVHealth_ToolConfig TENANT_CONFIG item created.');
        }

        // ════════════════════════════════════════════════════════════════════
        // 2. WVHealth_CoreSettings  (normally lazily created by CoreSettingsService)
        //    Pre-provisioning here ensures it lands on the correct primary site.
        // ════════════════════════════════════════════════════════════════════
        console.log('[sharePointService] Provisioning WVHealth_CoreSettings...');
        const coreSettingsListId = await ensureList('WVHealth_CoreSettings');

        await ensureColumns(coreSettingsListId, [
            { name: 'ConfigKey', displayName: 'Config Key', type: 'text' },
            { name: 'ConfigValue', displayName: 'Config Value', type: 'multiline' },
            { name: 'UpdatedAt', displayName: 'Updated At', type: 'dateTime' },
        ]);

        // Seed the default analysis config item if missing
        const csItems = await client.api(`/sites/${siteId}/lists/${coreSettingsListId}/items`)
            .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
            .expand('fields')
            .filter("fields/ConfigKey eq 'COPILOT_ANALYSIS_CONFIG'")
            .get();

        if (!csItems.value || csItems.value.length === 0) {
            const defaultConfig = {
                modelId: 'Snowflake/snowflake-arctic-embed-xs',
                cachingStrategy: 'Local',
                maxTotalIndexItems: 1000,
                readabilityClampMin: 40,
                readabilityClampMax: 90,
                readinessWeights: { structure: 0.5, readability: 0.3, base: 20 },
                layoutThresholds: { warning: 0.19, fail: 0.25 },
                keywordStuffing: { ttrThreshold: 0.4, ngramTopFrequency: 5 },
                excludedFolderPaths: ['Templates'],
                assessPublishedOnly: true,
            };
            await client.api(`/sites/${siteId}/lists/${coreSettingsListId}/items`).post({
                fields: {
                    Title: 'COPILOT_ANALYSIS_CONFIG',
                    ConfigKey: 'COPILOT_ANALYSIS_CONFIG',
                    ConfigValue: JSON.stringify(defaultConfig),
                    UpdatedAt: new Date().toISOString()
                }
            });
            console.log('[sharePointService] WVHealth_CoreSettings default item created.');
        }

        // ════════════════════════════════════════════════════════════════════
        // 3. WV_SearchEmbeddingsCache
        //    This list was never provisioned during onboarding, causing it to
        //    be created on the wrong site (or not at all). Fixed here.
        // ════════════════════════════════════════════════════════════════════
        console.log('[sharePointService] Provisioning WV_SearchEmbeddingsCache...');
        const embeddingsListId = await ensureList('WV_SearchEmbeddingsCache');

        await ensureColumns(embeddingsListId, [
            { name: 'ContentHash', displayName: 'Content Hash', type: 'text' },
            { name: 'ModelName', displayName: 'Model Name', type: 'text' },
            { name: 'Intent', displayName: 'Intent', type: 'text' },
            { name: 'IntentKey', displayName: 'Intent Key', type: 'text' },
            { name: 'Category', displayName: 'Category', type: 'text' },
            { name: 'CategoryKey', displayName: 'Category Key', type: 'text' },
            { name: 'CategoryConfidence', displayName: 'Category Confidence', type: 'number' },
            { name: 'Keywords', displayName: 'Keywords', type: 'multiline' },
            { name: 'DeepVector', displayName: 'Deep Vector', type: 'multiline' },
            { name: 'Readiness', displayName: 'Readiness', type: 'number' },
            { name: 'Readability', displayName: 'Readability', type: 'number' },
            { name: 'StructureScore', displayName: 'Structure Score', type: 'number' },
            { name: 'AEW', displayName: 'AEW', type: 'number' },
            { name: 'MatrixQuadrant', displayName: 'Matrix Quadrant', type: 'text' },
            { name: 'AnalysisReport', displayName: 'Analysis Report', type: 'multiline' },
            { name: 'SourcePageId', displayName: 'Source Page Id', type: 'number' },
        ]);

        console.log('[sharePointService] All required lists provisioned successfully on:', siteUrl);
        return { success: true };

    } catch (error: any) {
        console.error('[sharePointService] Provisioning failed:', error);

        // Handle 403 Access Denied
        if (error.statusCode === 403 || error.code === 'accessDenied' || (error.message && error.message.includes('Access denied'))) {
            const { path } = parseSiteUrl(siteUrl);
            const isRootSite = path === '/' || path === '';
            const rootSiteWarning = isRootSite
                ? ' (Note: The Root Site often has stricter permissions. Try a sub-site specific to this tool.)'
                : '';
            return {
                success: false,
                error: `Access Denied. Ensure you are a Site Owner or Site Admin.${rootSiteWarning}`
            };
        }

        return { success: false, error: error.message || 'Provisioning failed' };
    }
}

/**
 * @deprecated Use provisionAllRequiredLists instead.
 * Kept for backward compatibility — delegates to provisionAllRequiredLists.
 */
export const provisionToolConfigList = provisionAllRequiredLists;


/**
 * Load configured sites from WVHealth_ToolConfig SharePoint list (TENANT_CONFIG item)
 * @param siteUrl - Primary SharePoint site URL (hosts the WVHealth_ToolConfig list)
 * @returns Configured sites from AllowedSiteUrls field, or empty array if not found
 */
export async function loadConfiguredSitesFromToolConfig(
    siteUrl: string
): Promise<{ success: boolean; sites?: Array<{ url: string; isPrimary: boolean; siteName?: string }>; error?: string }> {
    try {
        const client = await getGraphClient();
        const { hostname, path } = parseSiteUrl(siteUrl);

        // 1. Get Site ID
        const siteIds = await client.api(`/sites/${hostname}:${path}`).select('id').get();
        if (!siteIds || !siteIds.id) {
            throw new Error('Could not resolve site ID');
        }
        const siteId = siteIds.id;
        const listName = 'WVHealth_ToolConfig';

        // 2. Get List ID
        const lists = await client.api(`/sites/${siteId}/lists`)
            .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
            .filter(`displayName eq '${listName}'`)
            .select('id')
            .get();

        if (!lists.value || lists.value.length === 0) {
            console.warn('[sharePointService] List does not exist yet, returning empty sites array');
            return { success: true, sites: [] };
        }

        const listId = lists.value[0].id;

        // 3. Query TENANT_CONFIG item
        const items = await client.api(`/sites/${siteId}/lists/${listId}/items`)
            .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
            .expand('fields')
            .filter("fields/ConfigKey eq 'TENANT_CONFIG'")
            .get();

        if (!items.value || items.value.length === 0) {
            console.warn('[sharePointService] TENANT_CONFIG item not found, returning empty sites array');
            return { success: true, sites: [] };
        }

        const tenantConfigFields = items.value[0].fields;
        const allowedSiteUrlsJson = tenantConfigFields.AllowedSiteUrls || '[]';
        const primarySiteUrl = tenantConfigFields.PrimarySiteUrl || '';

        // 4. Parse AllowedSiteUrls JSON array
        let allowedSiteUrls: string[] = [];
        try {
            allowedSiteUrls = JSON.parse(allowedSiteUrlsJson);
        } catch (error) {
            console.error('[sharePointService] Failed to parse AllowedSiteUrls JSON:', error);
            return { success: false, error: 'Failed to parse site configuration' };
        }

        // 5. Map to site entries with isPrimary flag
        const sites = allowedSiteUrls.map((url) => ({
            url,
            isPrimary: url.toLowerCase() === primarySiteUrl.toLowerCase(),
            siteName: undefined, // Site names are not stored, will be fetched on demand
        }));

        console.log(`[sharePointService] Loaded ${sites.length} configured sites from ToolConfig`);
        return { success: true, sites };

    } catch (error: any) {
        console.error('[sharePointService] Failed to load configured sites:', error);
        return { success: false, error: error.message || 'Failed to load configured sites' };
    }
}

/**
 * Retry helper for Graph API calls with exponential backoff
 */
async function retryGraphApiCall<T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    initialDelayMs: number = 1000
): Promise<T> {
    let lastError: any;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
            return await operation();
        } catch (error: any) {
            lastError = error;
            const isRetryable = error.statusCode === 503 || error.statusCode === 429 || error.code === 'serviceNotAvailable';

            if (!isRetryable || attempt === maxRetries - 1) {
                throw error;
            }

            const delayMs = initialDelayMs * Math.pow(2, attempt);
            console.warn(`[sharePointService] Retryable error (${error.statusCode}), attempt ${attempt + 1}/${maxRetries}, waiting ${delayMs}ms...`);
            await new Promise(resolve => setTimeout(resolve, delayMs));
        }
    }
    throw lastError;
}

/**
 * Save configured sites to WVHealth_ToolConfig SharePoint list (TENANT_CONFIG item)
 * Updates the AllowedSiteUrls field with current site list.
 * 
 * @param siteUrl - Primary SharePoint site URL (hosts the WVHealth_ToolConfig list)
 * @param sites - Array of site entries to save (from ConnectSharePoint component)
 * @param primarySiteUrl - URL of the primary site
 * @returns Success/failure result
 */
export async function saveConfiguredSitesToToolConfig(
    siteUrl: string,
    sites: Array<{ url: string; isPrimary: boolean; siteName?: string }>,
    primarySiteUrl: string
): Promise<{ success: boolean; error?: string }> {
    try {
        console.log('[sharePointService] Starting saveConfiguredSitesToToolConfig', { siteUrl, sitesCount: sites.length, primarySiteUrl });
        const client = await getGraphClient();
        const { hostname, path } = parseSiteUrl(siteUrl);

        // 1. Get Site ID
        const siteIds = await client.api(`/sites/${hostname}:${path}`).select('id').get();
        if (!siteIds || !siteIds.id) {
            throw new Error('Could not resolve site ID');
        }
        const siteId = siteIds.id;
        const listName = 'WVHealth_ToolConfig';

        // 2. Get List ID
        const lists = await client.api(`/sites/${siteId}/lists`)
            .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
            .filter(`displayName eq '${listName}'`)
            .select('id')
            .get();

        if (!lists.value || lists.value.length === 0) {
            return { success: false, error: 'WVHealth_ToolConfig list does not exist. Please provision it first.' };
        }

        const listId = lists.value[0].id;

        // 3. Query TENANT_CONFIG item
        const items = await client.api(`/sites/${siteId}/lists/${listId}/items`)
            .header('Prefer', 'HonorNonIndexedQueriesWarningMayFailRandomly')
            .expand('fields')
            .filter("fields/ConfigKey eq 'TENANT_CONFIG'")
            .get();

        if (!items.value || items.value.length === 0) {
            return { success: false, error: 'TENANT_CONFIG item not found. Please run onboarding first.' };
        }

        const itemId = items.value[0].id;
        console.log('[sharePointService] Found TENANT_CONFIG item:', itemId);

        // 4. Extract site URLs (only save URLs, not full SiteEntry objects)
        const allowedSiteUrls = sites.map(s => s.url);
        const allowedSiteUrlsJson = JSON.stringify(allowedSiteUrls);

        console.log('[sharePointService] Preparing to update fields:', {
            allowedSiteUrls,
            primarySiteUrl,
            jsonLength: allowedSiteUrlsJson.length
        });

        // 5. Update item with new AllowedSiteUrls (with retry logic)
        await retryGraphApiCall(async () => {
            const updatePayload = {
                AllowedSiteUrls: allowedSiteUrlsJson,
                PrimarySiteUrl: primarySiteUrl,
            };

            console.log('[sharePointService] Calling Graph API PATCH:', `/sites/${siteId}/lists/${listId}/items/${itemId}/fields`);

            return await client.api(`/sites/${siteId}/lists/${listId}/items/${itemId}/fields`)
                .patch(updatePayload);
        });

        console.log('[sharePointService] Successfully saved configured sites to ToolConfig');
        return { success: true };

    } catch (error: any) {
        console.error('[sharePointService] Failed to save configured sites:', {
            message: error.message,
            statusCode: error.statusCode,
            code: error.code,
            body: error.body,
            stack: error.stack
        });

        // Provide more specific error messages
        let errorMsg = 'Failed to save configured sites';
        if (error.statusCode === 503) {
            errorMsg = 'SharePoint service temporarily unavailable. Please try again in a moment.';
        } else if (error.statusCode === 429) {
            errorMsg = 'Too many requests. Please wait a moment and try again.';
        } else if (error.statusCode === 403) {
            errorMsg = 'Access denied. You may not have permission to update this list.';
        } else if (error.message) {
            errorMsg = error.message;
        }

        return { success: false, error: errorMsg };
    }
}
